<template>
    <div class="ns-tier-group-card tx-font-primary" :class="[ 'tg-style-' + configuration.style, 'tg-layout-' + layout ]" :id="`${baseId}-template`">
        <div class="tg-background" :id="`${baseId}-background`">
            <div v-if="!isMobile" class="tg-streamer" :id="`${baseId}-background-streamer`"></div>
            <div class="tg-container" :id="`${baseId}-ribbon-container`">
                <div
                    v-if="!isMobile"
                    class="tg-hero"
                    :id="`${baseId}-ribbon-hero`"
                    :style="imageStyling"
                >
                </div>
                <div
                    v-if="configuration.showRibbon && !isMobile && $nsLanguage.hasTranslation(configuration.context + '.ribbon')"
                    class="tg-ribbon"
                    :id="`${baseId}-ribbon-content-container`"
                >
                    <div class="tg-ribbon-text tx-font-primary text-uppercase" :id="`${baseId}-ribbon-content`">
                      {{ $nsLanguage.getTranslation(configuration.context + '.ribbon') }}
                    </div>
                </div>
            </div>
        </div>
        <section class="tg-content" :id="`${baseId}-container`" >
            <div class="tg-head" :id="`${baseId}-container-head`">
                <div
                    v-if="configuration.showHeadIcon && $nsLanguage.hasTranslation(configuration.context + '.headIcon')"
                    class="tg-head-icon"
                    :id="`${baseId}-container-head-icon-container`"
                >
                    <img :src="$nsLanguage.getTranslation(configuration.context + '.headIcon')" :id="`${baseId}-container-head-icon`" />
                </div>
                <h1 class="mt-4 tg-head-text tx-font-secondary" :id="`${baseId}-container-head-icon-landing-title`"
                >{{ $nsLanguage.getTranslation(configuration.context + '.landingTitle') }}</h1>
            </div>
            <div
                class="tg-body-text tx-font-primary tx-em-bold-800 tx-em-italic"
                v-html="$nsLanguage.getTranslation(configuration.context + '.bodyText')"
                :id="`${baseId}-container-body-text`"
            >
            </div>
            <div class="tg-tier-content" :id="`${baseId}-container-cards-container`">
                <template v-if="isMobile">
                    <!-- Mobile -->
                    <ns-tier-card
                        v-for="(tier, index) in tiers"
                        :key="index"
                        class="tg-tier-card"
                        :layout="layout"
                        :shadow="true"
                        :togglable="togglable"
                        :type="tier.tierType"
                        :color="getTierColor(tier, configuration.defaultTierColor)"
                        :icon="getTierIcon(tier, configuration.defaultTierIcon)"
                        :configuration="configuration"
                        :content="tier"
                        :parent-id="`${baseId}-container-cards-card-${index}-mobile`"
                    >
                        <!-- VIP -->
                        <template v-if="tier.tierType == 'vip'" #additional>
                            <div class="tgtc-additional" :id="`${baseId}-container-cards-${index}-container-vip`">
                                <div class="tgtc-link" :id="`${baseId}-container-cards-${index}-container-vip-link`">
                                    <span class="text-capitalize" :id="`${baseId}-container-cards-${index}-container-vip-link-call-text`">
                                        {{ $nsLanguage.getTranslation(configuration.context + '.call') }}
                                        <a
                                            :href="getTelephoneLink($nsLanguage.getTranslation(configuration.context + '.vipTelephone'))"
                                            :id="`${baseId}-container-cards-container-vip-call-phone-link`"
                                        >
                                            {{ $nsLanguage.getTranslation(configuration.context + '.vipTelephone') }}
                                        </a>
                                    </span>
                                </div>
                            </div>
                        </template>
                        <!-- Locked/AE -->
                        <template v-else-if="canUnlockTier(index) && tier.showUnlockRule && tier.tierType != 'vip'" #additional>
                            <div class="tgtc-additional" :id="`${baseId}-container-mobile-cards-${index}-container-non-vip-can-unlock-container`">
                                <h4 class="text-uppercase" :id="`${baseId}-container-mobile-cards-${index}-container-non-vip-can-unlock-title`">
                                    {{ $nsLanguage.getTranslation(configuration.context + '.unlockTierTitle') }}
                                </h4>
                                <template v-if="tier.unlockText">
                                    <p
                                        class="text-center tx-gray-light ma-0"
                                        v-if="tier.unlockLink"
                                        :id="`${baseId}-container-cards-${index}-container-non-vip-can-unlock-unlock-text-link-container`"
                                    >
                                        <a
                                            class="summary-link"
                                            :href="mapLink(tier.unlockLink)" v-html="$nsLanguage.getTranslation(configuration.context + '.' + tier.unlockText, tier)"
                                            @click="publishEvent($event, tier)"
                                            :id="`${baseId}-container-cards-${index}-container-non-vip-can-unlock-unlock-link`"
                                        >
                                        </a>
                                    </p>
                                    <p
                                      class="text-center tx-gray-light tx-em-bold-800 ma-0"
                                      v-else
                                      v-html="$nsLanguage.getTranslation(configuration.context + '.' + tier.unlockText, tier)"
                                      :id="`${baseId}-container-cards-${index}-container-non-vip-can-unlock-unlock-text-no-link`"
                                    >
                                    </p>
                                </template>
                                <div
                                  class="tc-button-container"
                                  v-if="tier.unlockButtonText"
                                  :id="`${baseId}-container-cards-${index}-container-non-vip-can-unlock-unlock-button-container`"
                                >
                                    <a
                                      class="tx-dec-none"
                                      :href="mapLink(tier.unlockButtonLink)"
                                      :id="`${baseId}-container-cards-${index}-container-non-vip-can-unlock-unlock-button-link`"
                                    >
                                        <button class="primary-solid fluid" :id="`${baseId}-container-cards-${index}-container-non-vip-can-unlock-unlock-button-button`">
                                            {{ $nsLanguage.getTranslation(configuration.context + '.' + tier.unlockButtonText) }}
                                        </button>
                                    </a>
                                </div>
                            </div>
                        </template>
                    </ns-tier-card>
                </template>
                <template v-else>
                    <!-- Tablet/Desktop -->
                    <ns-tier-card
                        v-for="(tier, index) in tiers"
                        :key="index"
                        :layout="layout"
                        :shadow="true"
                        :togglable="togglable"
                        :type="tier.tierType"
                        :color="getTierColor(tier, configuration.defaultTierColor)"
                        :icon="getTierIcon(tier, configuration.defaultTierIcon)"
                        :configuration="configuration"
                        :content="tier"
                        :parent-id="`${baseId}-container-cards-card-${index}-non-mobile`"
                    >
                        <!-- VIP -->
                        <template v-if="tier.tierType == 'vip'" #additional>
                            <div class="tgtc-additional" :id="`${baseId}-container-nonmobile-cards-${index}-container-vip-container`">
                                <div class="tgtc-link" :id="`${baseId}-container-nonmobile-cards-${index}-container-vip-can-unlock-call-container`">
                                    <span class="text-capitalize" :id="`${baseId}-container-nonmobile-cards-${index}-container-vip-can-unlock-call-text`">
                                        {{ $nsLanguage.getTranslation(configuration.context + '.call') }}
                                        <a
                                            :href="getTelephoneLink($nsLanguage.getTranslation(configuration.context + '.vipTelephone'))"
                                            :id="`${baseId}-container-nonmobile-cards-${index}-container-non-vip-can-unlock-call-link`"
                                        >
                                            {{ $nsLanguage.getTranslation(configuration.context + '.vipTelephone') }}
                                        </a>
                                    </span>
                                </div>
                            </div>
                        </template>
                        <!-- Locked/AE -->
                        <template v-else-if="canUnlockTier(index) && tier.showUnlockRule && tier.tierType != 'vip'" #additional>
                            <div class="tgtc-additional" :id="`${baseId}-container-nonmobile-cards-${index}-container-non-vip-can-unlock-container`">
                                <h4 class="text-uppercase" :id="`${baseId}-container-nonmobile-cards-${index}-container-non-vip-can-unlock-title`">
                                    {{ $nsLanguage.getTranslation(configuration.context + '.unlockTierTitle') }}
                                </h4>
                                <template v-if="tier.unlockText">
                                    <p
                                        class="text-center tx-gray-light ma-0"
                                        v-if="tier.unlockLink"
                                        :id="`${baseId}-container-nonmobile-cards-${index}-container-non-vip-can-unlock-unlock-text-link-container`"
                                    >
                                        <a
                                            class="summary-link"
                                            :href="mapLink(tier.unlockLink)"
                                            v-html="$nsLanguage.getTranslation(configuration.context + '.' + tier.unlockText, tier)"
                                            @click="publishEvent($event, tier)"
                                            :id="`${baseId}-container-nonmobile-cards-${index}-container-non-vip-can-unlock-unlock-link`"
                                        >
                                        </a>
                                    </p>
                                    <p
                                        class="text-center tx-gray-light tx-em-bold-800 ma-0"
                                        v-else
                                        v-html="$nsLanguage.getTranslation(configuration.context + '.' + tier.unlockText, tier)"
                                        :id="`${baseId}-container-nonmobile-cards-${index}-container-non-vip-can-unlock-unlock-text-no-link`"
                                    >
                                    </p>
                                </template>
                                <div
                                    class="tc-button-container"
                                    v-if="tier.unlockButtonText"
                                    :id="`${baseId}-container-nonmobile-cards-${index}-container-non-vip-can-unlock-unlock-button-container`"
                                >
                                    <a
                                        class="tx-dec-none"
                                        :href="mapLink(tier.unlockButtonLink)"
                                        :id="`${baseId}-container-nonmobile-cards-${index}-container-non-vip-can-unlock-unlock-button-link`"
                                    >
                                        <button class="primary-solid fluid" :id="`${baseId}-container-nonmobile-cards-${index}-container-non-vip-can-unlock-unlock-button-button`">
                                            {{ $nsLanguage.getTranslation(configuration.context + '.' + tier.unlockButtonText) }}
                                        </button>
                                    </a>
                                </div>
                            </div>
                        </template>
                    </ns-tier-card>
                </template>
            </div>
        </section>
    </div>
</template>

<script>

import { formatPoints } from '../../../services/points'
import { RunConfigService } from '@nuskin/ns-util'

import NsTierCard from '@/components/Tiering/TierCard.vue'

export default {
    name: 'ns-tier-group-card',
    components: {
        'ns-tier-card': NsTierCard
    },
    props: {
        layout: {
            type: String,
            default: 'mobile',
            validator (value) {
                return ['mobile', 'tablet', 'desktop'].indexOf(value) >= 0
            }
        },
        links: {
            type: Object,
            default: function () {
                return {}
            }
        },
        parentId: {
            type: String,
            required: false
        },
        togglable: {
            type: Boolean,
            default: true
        },
        configuration: {
            type: Object,
            default: function () {
                return {}
            }
        },
        tiers: {
            type: Array,
            default: function () {
                return []
            }
        }
    },
    beforeMount () {
        const runConfig = RunConfigService.getRunConfig()
        const locale = runConfig.language + '_' + runConfig.country

        this.imageStyling = {
            '--tier-card-right-side-image': 'url(\'/content/dam/apps/loyalty/' + locale + '/tierCardRightSideImage.png\')',
            '--tier-card-left-side-image': 'url(\'/content/dam/apps/loyalty/' + locale + '/tierCardLeftSideImage.png\')'
        }
    },
    data () {
        return {
            openSwitches: [],
            imageStyling: {}
        }
    },
    computed: {
        isMobile () {
            return this.layout === 'mobile'
        },
        isTablet () {
            return this.layout === 'tablet'
        },
        baseId: function () {
            const defaultBase = 'landing-tier-group-card'
            if (this.parentId) {
                return `${this.parentId}-${defaultBase}`
            } else {
                return defaultBase
            }
        }
    },
    methods: {
        currency (value) {
            return formatPoints(value)
        },
        canUnlockTier (index) {
            return this.tiers[index].unlockable
        },
        getTelephoneLink (text) {
            return 'tel:' + text.replace(/[^\d]+/g, '')
        },
        getTierColor (tier, defaultColor) {
            return tier.color || defaultColor || 'gray'
        },
        getTierIcon (tier, defaultIcon) {
            return tier.icon || defaultIcon
        },
        mapLink (link) {
            return this.links[link]
        }
    },
    created () {
        this.openSwitches = this.tiers.map(() => false)
    }
}

</script>

<style lang="scss" scoped>

@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/buttons";
@use "~@nuskin/ns-core-styles/src/scss/typographyVariables";

$color-blue: #02B2DF;

a {
    color: $color-blue !important;
    text-decoration: none;

    &:hover {
        text-decoration: underline;
    }

    button {
        text-decoration: none !important;
    }
}

.tx-em-bold-800 {
    ::v-deep {
        b, em, i {
            font-family: inherit;
            font-style: normal;
            font-weight: 800;
        }
    }
}

.tx-em-italic {
    ::v-deep {
        b, em, i {
            font-style: italic;
        }
    }
}

.ns-tier-group-card {
    $tg-width: 1004px;
    $tg-height: 346px;
    $tg-padding: 30px;

    $hero-width: 270px;

    $tg-content-margin: 140px;

    display: inline-block;
    position: relative;
    width: $tg-width;
    min-height: $tg-height + $tg-padding * 2;
    padding: $tg-padding;
    font-size: 16px;

    .tg-content {
        display: block;
        z-index: 100;
        position: relative;
        width: 100%;
        padding: 0;
        color: rgba(0, 0, 0, 0.85);
    }

    .tg-head {
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-content: center;
        align-items: center;
        margin: 25px 0 0 0;
    }

    .tg-head-icon {
        display: inline-block;
        margin-right: 0.50em;

        img {
            width: 76px;
        }
    }

    .tg-head-text {
        vertical-align: middle;
        line-height: 1.25em !important;
        padding: 0 !important;
        color: rgba(0, 0, 0, 0.85) !important;
        font-size: 36px !important;
        font-weight: 400 !important;
        letter-spacing: 2.22px !important;
        text-align: center;
        text-transform: none;
        white-space: nowrap;
    }

    .tg-body-text {
        width: 450px;
        margin: 13px auto 0 auto;
        font-size: 18px;
        line-height: 1.1666em;
    }

    .tg-tier-content {
        display: flex;
        flex-direction: row;
        justify-content: space-evenly;
        align-content: flex-start;
        align-items: flex-start;
        margin-top: 20px;
        margin-bottom: -20px;
    }

    .tg-tier-card-container {
        position: relative;
        box-shadow: 0 3px 6px 3px rgba(132, 129, 129, 0.25);
        display: flex;
        & + .tg-tier-card-container {
            margin-left: 16px;
        }

        .tg-tier-card {
            vertical-align: top;
        }
    }

    .tg-background,
    .tg-container,
    .tg-hero,
    .tg-ribbon,
    .tg-streamer {
        position: absolute;
    }

    .tg-background {
        top: $tg-padding - 2;
        left: $tg-padding - 2;
        right: $tg-padding - 2;
        height: $tg-height + 4;
        background-image: linear-gradient(to top right, #C2D42E 0%, #02B2DF 100%);
    }

    .tg-container {
        top: 2px;
        left: 2px;
        right: 2px;
        height: $tg-height;
        background-color: #FFFFFF;
    }

    .tg-hero {
        pointer-events: none;
        top: 0;
        bottom: 0;
        width: $hero-width;
        background-clip: padding-box;
        background-origin: padding-box;
        background-position: center right;
        background-repeat: no-repeat;
        background-size: auto 100%;
    }

    .tg-ribbon {
        pointer-events: none;
        display: flex;
        flex-direction: row;
        justify-content: center;
        overflow: hidden;
        z-index: 30;
        top: 0;
        left: 0;
        width: 210px;
        height: 122px;
    }

    .tg-ribbon-text {
        transform: rotate(-30deg);
        transform-origin: center;

        display: block;
        position: absolute;
        top: 34px;
        left: -128px;
        text-align: center;
        width: 200%;
        padding: 8px;
        background-color: #000000;
        color: #FFFFFF;
        font-size: 14px;
        -moz-osx-font-smoothing: grayscale;
        -webkit-font-smoothing: antialiased;
        font-weight: 600;
    }

    .tg-streamer {
        z-index: 20;
        top: 2px;
        left: 2px;
        right: 2px;
        height: $tg-height;
        background-position: 0% 233px;
        background-repeat: repeat-x;
    }

    &.tg-style-hero-tiers {
        $hero-horizontal-offset: -48px;

        padding-left: ($hero-width - $tg-padding);

        .tg-streamer {
            left: -$tg-padding;
            right: -$tg-padding;
            background-image: url('~@/assets/Stripes_BlueGreen_Wavy.svg');
        }

        .tg-hero {
            z-index: 10;
            left: $hero-horizontal-offset;
            right: auto;
            background-image: var(--tier-card-left-side-image);
        }

        .tg-body-text {
            text-align: center;
        }
    }

    &.tg-style-tiers-hero {
        $hero-horizontal-offset: -$tg-padding - 4px;

        padding-right: ($hero-width - $tg-padding);

        .tg-streamer {
            z-index: 10;
            background-image: url('~@/assets/Stripe_SmallDashes_BlueGreen.svg');
        }

        .tg-hero {
            top: -$tg-padding;
            bottom: -$tg-padding;
            left: auto;
            right: $hero-horizontal-offset;
            background-image: var(--tier-card-right-side-image);
        }

        .tg-head {
            margin-left: auto;
            margin-right: auto;
        }

        .tg-body-text {
            text-align: left;
        }
    }

    &.tg-layout-tablet {
        $tg-width: 740px;
        $tg-height: auto;
        $tg-padding: 14px;

        width: $tg-width;
        margin-top: 30px;
        padding: $tg-padding;

        .tg-background {
            top: $tg-padding - 2;
            left: $tg-padding - 2;
            right: $tg-padding - 2;
            height: $tg-height + 4;
        }

        &.tg-style-hero-tiers,
        &.tg-style-tiers-hero {
            .tg-head {
                margin: 25px 120px 0 120px;
            }
        }

        &.tg-style-hero-tiers {
            .tg-container {
                overflow: hidden;
            }

            .tg-streamer {
                left: -$tg-padding;
                right: -$tg-padding;
            }

            .tg-hero {
                left: -120px;
                background-image: var(--tier-card-left-side-image);
            }

            .tg-body-text {
                text-align: center;
            }
        }

        &.tg-style-tiers-hero {
            .tg-hero {
                right: -80px;
            }

            .tg-body-text {
                text-align: center;
            }
        }
    }

    &.tg-layout-mobile {
        $tg-height: auto;

        $tg-content-margin: 118px;

        width: 100%;
        padding: 0;

        & + .ns-tier-group-card {
            margin-top: 25px;
        }

        .tg-background {
            top: 0;
            bottom: 0;
            left: 0;
            right: 0;
            height: auto;
        }

        .tg-container {
            overflow: hidden;
            bottom: 2px;
            height: auto;
        }

        .tg-tier-card-container {
            & + .tg-tier-card-container {
                margin-top: 15px;
                margin-left: 0;
            }
        }

        &.tg-style-hero-tiers,
        &.tg-style-tiers-hero {
            .tg-content {
                width: 100%;
                margin: 0;
                padding: 14px 15px 20px;
            }

            .tg-tier-content {
                flex-direction: column;
                align-content: stretch;
                align-items: stretch;
            }

            .tg-head {
                width: 100%;
                margin-top: 0;
                margin-bottom: 0;
            }

            .tg-head-text {
                font-size: 30px !important;
                letter-spacing: 1.85px !important;
                white-space: normal;
            }

            .tg-body-text {
                width: 100%;
                margin-left: 0;
                margin-right: 0;
                font-weight: 300;
                line-height: 1.3125em;
                text-align: center;
            }

            .tg-tier-content {
                margin-top: 20px;
                margin-bottom: 0;
            }
        }
    }
}

.tgtc-additional {
    button {
        font-size: 16px;
        line-height: 1.0625em;
    }

    h1, h2, h3, h4, h5, h6 {
        margin: 0 0 10px 0;
        color: #272525;
        font-size: 14px;
        font-weight: bold;
        line-height: 1.2857em;
        text-align: center;
    }

    p {
        margin: 5px 0 0 0;
        color: #7B7C81;
        font-size: 12px;
        font-weight: normal;
        text-align: center;
        line-height: 1.44em;
    }

    .tgtc-link {
        padding: 8px 15px;
        background-color: #FFFFFF;
        color: #63666A;
        font-size: 14px;
        font-weight: normal;
        line-height: 1.5em;
        text-align: center;
    }

    .tc-button-container {
        margin-top: 15px;
    }
}

</style>

<style lang="scss">

.ns-tier-card.tc-type-vip.tc-additional {
    .tc-body-content {
        padding: 0 !important;
    }

    .tc-section {
        padding: 0 !important;
    }
}

</style>
